"use client";

import clsx from "clsx";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";

import { ChevronRightIcon } from "../dx-icons";

import { ImageViewer } from "./_components/image-viewer";
import { useHorizontalDragGuard } from "./_hooks/use-horizontal-drag-guard";

type Props = {
  images: { url: string }[] | undefined;
  altText: string;
  /**
   * 先頭 Hero 画像を priority (eager) で読むか。default true。
   * 一覧で多数の MainVisual を並べる場合、全部 priority だと LCP が悪化するため、
   * above-the-fold の 1 件以外は false を渡す。
   */
  heroPriority?: boolean;
  /**
   * サムネイル選択列を表示するか。default true。
   * 一覧カード等の省スペースなレイアウトでは Hero のみ表示したい場合に false を渡す。
   */
  showThumbnails?: boolean;
  /**
   * Hero 画像タップ時に画像拡大モーダルの代わりに呼ぶハンドラ。
   * 指定時は PC/SP 問わず常にこちらを呼び、ImageViewer は開かない（サムネ切り替えには影響しない）。
   */
  onImageClick?: () => void;
};

/** Hero のアスペクト比 (3:2)。`aspect-[3/2]` をクラスで指定する */
/** 選択中サムネのサイズ */
const THUMB_SIZE_ACTIVE = 60;
/** 非選択サムネのサイズ。Active との差で選択状態を表す */
const THUMB_SIZE_INACTIVE = 56;
const THUMB_GAP = 4;
/** 画像が無いときに並べるプレースホルダー数 (モック表示) */
const PLACEHOLDER_COUNT = 9;
const LG_MIN_WIDTH = 1024;

/**
 * メインビジュアル + サムネイル列。
 *
 * - Hero: embla-carousel でスワイプ / マウスドラッグで切り替え
 *   - `slidesToScroll: 1` + `dragFree: false` で 1 枚ずつ確実にスナップ
 *   - `watchDrag: true` でモバイルタッチ・PC マウス両対応
 * - サムネ: 64×64、タップで Hero を該当インデックスへスクロール
 * - Hero ↔ サムネ間で currentIndex を embla の `select` イベントで同期
 * - SP タップで全画面モーダル拡大 (<1024px のみ)
 * - サムネ hover で dim overlay 解除
 * - PC hover でサムネ列の送り矢印表示
 */
export function MainVisual({
  images,
  altText,
  heroPriority = true,
  showThumbnails = true,
  onImageClick,
}: Props) {
  const items: Array<{ url: string } | null> =
    images && images.length > 0
      ? images
      : Array.from({ length: PLACEHOLDER_COUNT }, () => null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    dragFree: false,
    containScroll: "trimSnaps",
    watchDrag: true,
  });
  // サムネ列にも embla を当てて PC のドラッグスクロールに対応。
  // `dragFree: true` で snap せず自由スクロール、タッチも同じハンドリング。
  const [thumbEmblaRef, thumbEmblaApi] = useEmblaCarousel({
    dragFree: true,
    containScroll: "trimSnaps",
    watchDrag: true,
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // embla の選択中スナップを React state に同期
  useEffect(() => {
    if (!emblaApi) return;
    const handleSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    handleSelect();
    emblaApi.on("select", handleSelect);
    emblaApi.on("reInit", handleSelect);
    return () => {
      emblaApi.off("select", handleSelect);
      emblaApi.off("reInit", handleSelect);
    };
  }, [emblaApi]);

  // Hero swipe 等で active が変わった時、active なサムネが viewport 外なら scrollTo する。
  // click 時はそのサムネが必ず visible なのでこの分岐は発火しない (= 不要なジャンプを防ぐ)。
  useEffect(() => {
    if (!thumbEmblaApi) return;
    if (thumbEmblaApi.slidesInView().includes(currentIndex)) return;
    thumbEmblaApi.scrollTo(currentIndex);
  }, [thumbEmblaApi, currentIndex]);

  // Hero / サムネ 双方の embla viewport で、横優位の drag に対してのみ Drawer の
  // swipe-dismiss を抑止する。縦優位なら Drawer に到達させて dismiss を許可。
  const heroDragGuard = useHorizontalDragGuard();
  const thumbDragGuard = useHorizontalDragGuard();

  const scrollHeroTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  // サムネ列の送り矢印 (PC hover)。 dragFree なので scroll でも可否を更新する。
  const [thumbCanPrev, setThumbCanPrev] = useState(false);
  const [thumbCanNext, setThumbCanNext] = useState(false);
  useEffect(() => {
    if (!thumbEmblaApi) return;
    const update = () => {
      setThumbCanPrev(thumbEmblaApi.canScrollPrev());
      setThumbCanNext(thumbEmblaApi.canScrollNext());
    };
    update();
    thumbEmblaApi
      .on("reInit", update)
      .on("select", update)
      .on("scroll", update);
    return () => {
      thumbEmblaApi
        .off("reInit", update)
        .off("select", update)
        .off("scroll", update);
    };
  }, [thumbEmblaApi]);
  const thumbScrollPrev = useCallback(
    () => thumbEmblaApi?.scrollPrev(),
    [thumbEmblaApi],
  );
  const thumbScrollNext = useCallback(
    () => thumbEmblaApi?.scrollNext(),
    [thumbEmblaApi],
  );

  // lg+ は Drawer/カードに余裕があるので拡大不要、SP のみ全画面モーダル。
  const openModalIfMobile = useCallback(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth >= LG_MIN_WIDTH) return;
    setModalOpen(true);
  }, []);

  const handleHeroClick = onImageClick ?? openModalIfMobile;

  const currentItem = items[currentIndex];

  return (
    <>
      {/* Hero: embla viewport (3:2 アスペクト比)。
          `heroDragGuard` で横優位の drag のみ Drawer の swipe-dismiss を抑止する。
          縦方向の drag は Drawer まで届くので、サムネ下から下方向スワイプで Drawer が閉じる。 */}
      <div
        ref={emblaRef}
        className="bg-dx-surface-subtle aspect-[3/2] w-full overflow-hidden"
        {...heroDragGuard}
      >
        {/* embla container: 横並びの slides */}
        <div className="flex h-full">
          {items.map((item, i) => (
            <div
              // 画像の表示切り替えだけで、行動の分析対象にならない
              data-analytics-ignore
              key={i}
              // lg+ は何もしないので preflight の cursor:pointer を打ち消す。
              className="relative h-full w-full shrink-0 grow-0 basis-full cursor-zoom-in select-none lg:cursor-default"
              onClick={handleHeroClick}
              role={item?.url && !onImageClick ? "button" : undefined}
              tabIndex={item?.url ? -1 : undefined}
              aria-label={
                item?.url && !onImageClick ? "画像を拡大表示" : undefined
              }
              // onImageClick 時は拠点名見出しの button と開く対象・アクセシブルネームが重複するため隠す。
              aria-hidden={onImageClick ? true : undefined}
            >
              {item?.url && (
                <Image
                  src={item.url}
                  alt={i === 0 ? altText : ""}
                  fill
                  // `cover`: 3:2 の枠にクロップして全面を埋める（余白を出さない）。
                  // 全体を確認したい場合は SP タップで全画面モーダル（object-contain）を開ける。
                  className="pointer-events-none object-cover object-center"
                  // Drawer は max-w 640px。それ以上の端末でも Hero は 640px に収まる前提。
                  sizes="(min-width: 640px) 640px, 100vw"
                  draggable={false}
                  // 1 枚目は priority (fetchPriority="high")、2 枚目以降は default
                  // (eager) で並行 fetch。heroPriority=false の場合は 1 枚目も lazy。
                  priority={heroPriority && i === 0}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* サムネイル列: embla で PC のドラッグスクロール + モバイルのタッチスクロール対応。
          行高は active 60px + paddingBlock 2 + 2 = 64px に固定する。
          固定しないとサイズトランジション中に「縮小中の旧 active」と「拡大中の新 active」が
          交差して一瞬両方とも 60 未満になり、コンテナ高さがガタつく。
          embla の viewport は `overflow: hidden` なので PC のスクロールバーも出ない。 */}
      {showThumbnails && (
        <div className="group/thumbs relative">
          <div
            ref={thumbEmblaRef}
            className="overflow-hidden"
            style={{ height: THUMB_SIZE_ACTIVE + 4 }}
            // Hero と同様、横優位の drag のみ Drawer の swipe-dismiss を抑制
            {...thumbDragGuard}
          >
            <div
              className="flex h-full items-center"
              // paddingInline なし: 先頭サムネの左端を hero と揃えるため。
              // paddingBlock 2: active/inactive のサイズ差 (60/56) による行高のガタつき防止。
              style={{ gap: THUMB_GAP, paddingBlock: 2 }}
            >
              {items.map((item, i) => {
                const isActive = i === currentIndex;
                const size = isActive ? THUMB_SIZE_ACTIVE : THUMB_SIZE_INACTIVE;
                return (
                  <button
                    // 画像の表示切り替えだけで、行動の分析対象にならない
                    data-analytics-ignore
                    key={i}
                    type="button"
                    onClick={() => scrollHeroTo(i)}
                    className="group/thumb bg-dx-surface-subtle relative shrink-0 cursor-pointer overflow-hidden transition-[width,height] duration-150"
                    style={{ width: size, height: size }}
                    aria-label={`画像 ${i + 1} を表示`}
                    aria-current={isActive}
                  >
                    {item?.url && (
                      <Image
                        src={item.url}
                        alt=""
                        fill
                        className="pointer-events-none object-cover"
                        // 表示は 60×60 だが Retina (2-3x) + 横長 (w > h) 画像での
                        // object-cover クロップ時の実効ピクセル不足を見越して 200px 相当を要求。
                        // 例: w/h=3/2 の元画像なら 200px の variant 高さは ~133px → 60×60 を 2x で
                        // 描画するのに十分。
                        sizes="200px"
                        // サムネは hero と帯域を競わないよう lazy。quality は default (75) を使い
                        // Retina 上での視認性を確保。
                        loading="lazy"
                        draggable={false}
                      />
                    )}
                    {/* 非アクティブは dim overlay、 hover で解除 (Figma ImageViewThumbsItem)。 */}
                    {item?.url && !isActive && (
                      <span className="pointer-events-none absolute inset-0 bg-[rgba(106,102,92,0.32)] transition-opacity duration-150 group-hover/thumb:opacity-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          {/* PC hover でサムネ列の左右送り矢印。 SP は swipe。 */}
          <div className="xs:flex pointer-events-none absolute inset-y-0 left-0 hidden items-center">
            {thumbCanPrev && (
              <div className="pointer-events-auto opacity-0 transition-opacity duration-150 group-hover/thumbs:opacity-100">
                <ThumbNavButton direction="prev" onClick={thumbScrollPrev} />
              </div>
            )}
          </div>
          <div className="xs:flex pointer-events-none absolute inset-y-0 right-0 hidden items-center">
            {thumbCanNext && (
              <div className="pointer-events-auto opacity-0 transition-opacity duration-150 group-hover/thumbs:opacity-100">
                <ThumbNavButton direction="next" onClick={thumbScrollNext} />
              </div>
            )}
          </div>
        </div>
      )}

      {modalOpen && currentItem?.url && (
        <ImageViewer
          imageUrl={currentItem.url}
          altText={`${altText}（${currentIndex + 1}枚目）`}
          caption={null}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

/** サムネ列の送り矢印ボタン。float 風 (rounded + shadow)。 */
function ThumbNavButton({
  direction,
  onClick,
}: {
  direction: "prev" | "next";
  onClick: () => void;
}) {
  return (
    <button
      // 画像の表示切り替えだけで、行動の分析対象にならない
      data-analytics-ignore
      type="button"
      onClick={onClick}
      aria-label={direction === "prev" ? "前のサムネイル" : "次のサムネイル"}
      className="bg-dx-surface-raised text-dx-text-default hover:bg-dx-bg-base-hover flex size-[32px] cursor-pointer items-center justify-center rounded-full shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_2px_6px_0_rgba(0,0,0,0.1)] transition-colors duration-150"
    >
      <ChevronRightIcon
        className={clsx("size-[20px]", direction === "prev" && "rotate-180")}
      />
    </button>
  );
}

/**
 * ロード中の placeholder。Hero (3:2) + サムネ列の構造を実体と同じ尺で並べる。
 * embla は使わず CSS のみで配置 (ロード中はインタラクション不要)。
 */
export function MainVisualSkeleton({
  thumbCount = PLACEHOLDER_COUNT,
  showThumbnails = true,
}: { thumbCount?: number; showThumbnails?: boolean } = {}) {
  return (
    <>
      <div className="bg-dx-surface-subtle aspect-[3/2] w-full animate-pulse" />
      {showThumbnails && (
        <div
          className="flex items-center py-[2px]"
          style={{ height: THUMB_SIZE_ACTIVE + 4, gap: THUMB_GAP }}
        >
          {Array.from({ length: thumbCount }).map((_, i) => {
            const size = i === 0 ? THUMB_SIZE_ACTIVE : THUMB_SIZE_INACTIVE;
            return (
              <div
                key={i}
                className="bg-dx-surface-subtle shrink-0 animate-pulse"
                style={{ width: size, height: size }}
              />
            );
          })}
        </div>
      )}
    </>
  );
}
